import type { CSSProperties } from 'react';
import { teboxDesignSystem } from '@boubou/tebox-design-system';

export type StandardPillSize = 'small' | 'medium';

/** Solid fill for Reserved + Active chips — exact hex you specified (booking dashboard). */
export const BOOKING_RESERVED_ACTIVE_BG = '#E5E7EB';

function parseHexRgb(hex: string): { r: number; g: number; b: number } | null {
  let s = hex.replace(/\s/g, '').replace(/^#/, '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  if (s.length !== 6) return null;
  const n = parseInt(s, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Label colour on solid `backgroundHex` (WCAG-ish luminance). */
export function bookingPillLabelOnBackground(backgroundHex: string): string {
  const norm = backgroundHex.replace(/\s/g, '').toLowerCase();
  if (norm === '#ecff41') return teboxDesignSystem.colors.text.primary;
  /** Paid / success — green is bright enough that luminance picks dark text; product wants white on this chip. */
  const successHex = teboxDesignSystem.components.pill.colors.success.replace(/\s/g, '').toLowerCase();
  if (norm === successHex) return teboxDesignSystem.colors.text.inverse;
  const gs = teboxDesignSystem.components.pill.colors.golferStatus;
  const pendingHex = gs.pendingReservation.replace(/\s/g, '').toLowerCase();
  const cancelledHex = gs.cancelledReservation.replace(/\s/g, '').toLowerCase();
  if (norm === pendingHex || norm === cancelledHex) return teboxDesignSystem.colors.text.inverse;
  const rgb = parseHexRgb(backgroundHex);
  if (!rgb) return teboxDesignSystem.colors.text.primary;
  const l = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  return l > 0.55 ? teboxDesignSystem.colors.text.primary : '#FFFFFF';
}

/**
 * Booking: **solid** `backgroundColor === accentHex` (exact — no lighten wash). Chip radius + padding from Tebox pill tokens.
 */
export function getStandardPillStyles(
  accentHex: string,
  size: StandardPillSize = 'small',
  overrides?: CSSProperties,
  /** @deprecated Ignored; backgrounds are always the exact `accentHex`. */
  _backgroundLighten?: number
): CSSProperties {
  return {
    ...teboxDesignSystem.components.pill.base,
    ...teboxDesignSystem.components.pill.sizes[size],
    borderRadius: teboxDesignSystem.components.pill.borderRadius,
    backgroundColor: accentHex,
    color: bookingPillLabelOnBackground(accentHex),
    border: 'none',
    ...overrides,
  };
}
